/*    
<summary>
  This functional component "AddStaffManagement" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:01-Jan-2025
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { cloneDeep } from "lodash";
import { useStore } from "../../../../contexts/store-provider";
import { IAddStaffManagement } from "../../../../models/forms/IAddStaff";
import { formatMessage } from "../../../../translations/format-message";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import StoreAddStaffManagementForm from "../forms/add-edit-store-staff-information-form";
import { getStoreIdFromLocalStorage } from "../../../../helpers/localstorage-helper";
import ConfirmPopupHOC from "../../../../shared-components/popup/confirm/confirm-popup-hoc";
import ConfirmPopup from "../../../../shared-components/popup/confirm/confirm-popup";

interface IProps {
  id: number;
  modalClosed: () => void;
  deletePopupToggleHandler: () => void;
  showDeletePopup: boolean;
  storeId: number;
  showConfirmPopup?: boolean;
  confirmPopupToggleHandler: () => void;
}

const StoreAddStaffManagement = (props: IProps) => {
  const { staffManagementStore, masterManagementStore, storeManagementStore } =
    useStore();
  const {
    GetStaffManagementByIdService,
    resetAddUpdateStaffManagementState,
    resetStaffManagementDetail,
    staffState,
    staffDetail,
    addUpdateStaffManagementState,
    AddStaffManagementService,
    UpdateStaffManagementService,
    DeleteStaffManagementService,
    resetDeleteStaffManagement,
    inProgress,
    deleteStaffManagementState,
    getIsEmailCheckedOrFetched,
    GetCheckIfStaffAlreadyExistInStore,
    resetIsEmailCheckedOrFetched
  } = staffManagementStore;

  const {
    GetAllStoreOptionForStaffService,
    getAllStoreOptionsForStaff,
    getAllStoreOptionsForStaffUpdate,
  } = masterManagementStore;
  const { storeState, resetPrefectureByZipCode } = storeManagementStore;
  const initialValues: IAddStaffManagement = staffDetail;
  const [values, setTempValues] = useState<any>(null);

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    GetAllStoreOptionForStaffService(props.id);
    if (props.id > 0) {
      GetStaffManagementByIdService(props.id, props.storeId);
    }
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateStaffManagementState.success) {
      setTempValues(null);
      if (props.id === -1)
        toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateStaffManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "staffDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (staffState.error) {
      toast.error(formatMessage(staffState.error));
    }
  }, [staffState.error]);
  
  useEffect(() => {
    if (addUpdateStaffManagementState.error) {
       if (
        addUpdateStaffManagementState.error == "StoreEmailAlreadyExist"
      ) {
        props.confirmPopupToggleHandler();
      } else {
        toast.error(formatMessage(addUpdateStaffManagementState.error));
      }
      resetAddUpdateStaffManagementState();
    }
  }, [addUpdateStaffManagementState.error]);

  useEffect(() => {
    if (deleteStaffManagementState.success) {
      onModalClosed();
      toast.success(formatMessage("deleted_success"));
      props.deletePopupToggleHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteStaffManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateStaffManagementState.error) {
      toast.error(formatMessage(addUpdateStaffManagementState.error));
      resetAddUpdateStaffManagementState();
    }
  }, [addUpdateStaffManagementState.error]);

  useEffect(() => {
    if (deleteStaffManagementState.error) {
      toast.error(formatMessage(deleteStaffManagementState.error));
      resetDeleteStaffManagement();
    }
  }, [deleteStaffManagementState.error]);

  /**
   * This function adds the staff when "Id < 0" and updates the Existing staff when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: IAddStaffManagement) => {
    let tempValues = cloneDeep(values);
    setTempValues(tempValues);
    tempValues.StaffName = values.StaffName.trim();
    tempValues.NominationFee = Number(tempValues.NominationFee);
    const storeId = getStoreIdFromLocalStorage() ?? null;

    if (getIsEmailCheckedOrFetched) {
      if (props.id < 0) {
        AddStaffManagementService(tempValues, false);
      } else {
        UpdateStaffManagementService(props.id, tempValues);
      }
    } else {
      GetCheckIfStaffAlreadyExistInStore({
        ...tempValues,
        Id: props.id,
        StoreId: tempValues.StoreId
          ? Number(tempValues.StoreId)
          : Number(storeId),
      });
    }
  };

  const onSubmitOverwriteForm = () => {
    let tempValues = cloneDeep(values);
    if (props.id < 0) {
        AddStaffManagementService({ ...tempValues }, true);
      } else {
        UpdateStaffManagementService(props.id, tempValues);
      }
  };

  /**
   * This function closes the pop up modal and reset getstaffdetails, addUpdate staff observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    resetStaffManagementDetail();
    resetPrefectureByZipCode();
    resetIsEmailCheckedOrFetched();
    props.modalClosed();
  };

  const deleteHandler = () => {
    DeleteStaffManagementService(props.id, props.storeId);
  };

  return (
    <React.Fragment>
      {props.showDeletePopup && (
        <DeletePopup
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          isLoading={false}
          title="delete_confirmation"
          message="are_you_sure_you_want_to_delete_this_staff"
        />
      )}
      {props.showConfirmPopup && (
        <ConfirmPopup
          isLoading={false}
          title="confirmation_popup_title"
          message="store_email_already_exist"
          modalSubmit={onSubmitOverwriteForm}
          modalClosed={props.confirmPopupToggleHandler}
        />
      )}
      <StoreAddStaffManagementForm
        initialValues={initialValues}
        id={props.id}
        storeId={props.storeId}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        getStaffManagementLoading={
          staffState.inProgress || storeState.inProgress || inProgress
        }
        addUpdateStaffManagementLoading={
          addUpdateStaffManagementState.inProgress
        }
        deleteStaffManagementLoading={deleteStaffManagementState.inProgress}
        deleteHandler={props.deletePopupToggleHandler}
        storeOptions={
          props.id > 0
            ? getAllStoreOptionsForStaffUpdate
            : getAllStoreOptionsForStaff
        }
      />
    </React.Fragment>
  );
};

export default ConfirmPopupHOC(DeletePopupHOC(observer(StoreAddStaffManagement)));
